import {
  getPermissionNamesByIds,
  getRoleNameById,
  getUserToRequest,
} from "../views/dashboard/settings/manage-coaches/utils/manage-coaches-utils";

/**
 * Returns an object containing various access permissions for a user in an organization.
 *
 * @param {Object} user - The user object.
 * @param {Object} organizationOwner - The organization owner object.
 * @param {Array} globalOrganizationPermissions - The global organization permissions array. Defaults to an empty array.
 * @param {Array} organizationRoles - The organization roles array. Defaults to an empty array.
 * @return {Object} An object containing the following properties:
 *   - userOrganizationCanAccessToWidgets: A boolean indicating if the user can access widgets in the organization.
 *   - userOrganizationCanAccessToExerciseLibrary: A boolean indicating if the user can access the exercise library in the organization.
 *   - userCanAccessToPush: A boolean indicating if the user can access push in the organization.
 *   - userOrganizationCanAccessToTemplates: A boolean indicating if the user can access templates in the organization.
 *   - userCanAccessToPress: A boolean indicating if the user can access press in the organization.
 *   - isAdminOrOwner: A boolean indicating if the user is an admin or owner in the organization.
 *   - isGym: A boolean indicating if the user is a gym in the organization.
 */
export const getUserAccess = (
  user,
  organizationOwner,
  globalOrganizationPermissions = [],
  organizationRoles = []
) => {
  const userIsCoach = user.permissions.includes("coach");
  const userToRequest = userIsCoach
    ? getUserToRequest(user, organizationOwner)
    : user;

  const { organizationPermissions = [], organizationId } = user;

  const { pushAccountInfo: isFe, pressAccountInfo } = userToRequest;

  //Get the name of the organization's role of the current user
  const roleName = !!user.organizationRoleId
    ? getRoleNameById(organizationRoles, user.organizationRoleId)
    : "Owner";

  //User's permissions (if it's part of an organization, if not, we only are giving a empty array)
  const userOrganizationPermission =
    organizationPermissions.length > 0
      ? getPermissionNamesByIds(
          globalOrganizationPermissions,
          organizationPermissions
        )
      : [""];

  //Constants to detect the user permissions, if the user not belongs to an organization, is able to see the option, and if it's part of an organization
  //We have to check if the user has the permissions
  const userOrganizationCanAccessToWidgets =
    !organizationId ||
    (!!organizationId && userOrganizationPermission.includes("Widgets"));

  const userOrganizationCanAccessToExerciseLibrary =
    !organizationId ||
    (!!organizationId &&
      userOrganizationPermission.includes("Exercise Library"));

  const userCanAccessToPush =
    (!organizationId && isFe) ||
    (isFe && !!organizationId && userOrganizationPermission.includes("PUSH"));

  const userOrganizationCanAccessToTemplates =
    !organizationId ||
    (!!organizationId && userOrganizationPermission.includes("Templates"));
  const userCanAccessToPress =
    (!organizationId && !!pressAccountInfo) ||
    (isFe && !!organizationId && userOrganizationPermission.includes("PRESS"));
  const isAdminOrOwner = roleName === "Owner" || roleName === "Admin";
  const isGym = !!pressAccountInfo && userCanAccessToPress;

  return {
    userOrganizationCanAccessToWidgets,
    userOrganizationCanAccessToExerciseLibrary,
    userCanAccessToPush,
    userOrganizationCanAccessToTemplates,
    userCanAccessToPress,
    isAdminOrOwner,
    isGym,
  };
};
